/**
 * LoginPage - Microsoft 365 Style
 * Professional login form with Fluent UI components
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Input, Button, Label, makeStyles, Text, Spinner } from '@fluentui/react-components';
import { EyeRegular, EyeOffRegular, PersonRegular, LockClosedRegular } from '@fluentui/react-icons';
import { ApiClient, newToast, normalizeImageUrl } from '../../configurations/index.config';
import { useAppContext } from '../../contexts/AppContainer';
import { getErrorMessage } from '../../types';
import defaultLogo from './../../images/logo.png';

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#008272', // M365 teal
    padding: '24px 16px',
  },
  loginCard: {
    maxWidth: '480px',
    width: '100%',
    padding: '40px 32px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logo: {
    height: '48px',
    width: 'auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    textAlign: 'center',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#605e5c',
    textAlign: 'center',
    marginBottom: '32px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#323130',
  },
  input: {
    width: '100%',
  },
  passwordContainer: {
    position: 'relative',
  },
  eyeButton: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: '#605e5c',
    border: 'none',
    background: 'transparent',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    ':hover': {
      color: '#323130',
    },
  },
  submitButton: {
    width: '100%',
    marginTop: '8px',
  },
  footer: {
    marginTop: '24px',
    textAlign: 'center',
    fontSize: '12px',
    color: '#605e5c',
  },
});

interface FormData {
  username: string;
  password: string;
}

export const LoginPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { saveUser } = useAppContext() as any;
  const [formData, setFormData] = useState<FormData>({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyLogo = async () => {
      try {
        const response = await ApiClient.get('/api/v1/company-setup');
        if (response.data.company?.logo_url) {
          setCompanyLogo(normalizeImageUrl(response.data.company.logo_url) || null);
        }
      } catch (error) {
        // Silently fail, will use default logo
      }
    };
    fetchCompanyLogo();
  }, []);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const { username, password } = formData;

    if (!username || !password) {
      newToast('warning', 'Please fill in all fields.');
      return;
    }

    setLoading(true);
    try {
      const response = await ApiClient.post('/login-admin-user', { username, password });
      const data = response.data;

      if (data.status === 'success' && data.data) {
        const { user, token, sessionToken } = data.data;

        // Store tokens
        localStorage.setItem('sessionToken', sessionToken);

        // Save user data and auth token
        saveUser(token, user);
        newToast('success', 'Login successful!');
        navigate('/account', { replace: true });
      } else {
        newToast('error', data.message || 'Login failed');
      }
    } catch (error: any) {
      // Enhanced error handling for different HTTP status codes
      if (error.response) {
        switch (error.response.status) {
          case 400:
            newToast('error', 'Please fill in all required fields');
            break;
          case 401:
            newToast('error', 'Invalid username or password');
            break;
          case 403:
            newToast(
              'error',
              'Your account has been deactivated. Please contact your administrator.'
            );
            break;
          case 500:
            newToast('error', 'Server error. Please try again later.');
            break;
          default:
            newToast('error', error.response.data?.message || 'Login failed. Please try again.');
        }
      } else if (error.request) {
        newToast('error', 'Network error. Please check your connection.');
      } else {
        newToast('error', getErrorMessage(error));
      }
    }
    setLoading(false);
  };

  return (
    <div className={styles.container}>
      <Card className={styles.loginCard}>
        {/* Logo */}
        <div className={styles.logoContainer}>
          <img src={companyLogo || defaultLogo} alt="Logo" className={styles.logo} />
        </div>

        {/* Title */}
        <Text className={styles.title}>Security Admin Center</Text>
        <Text className={styles.subtitle}>Sign in to continue</Text>

        {/* Login Form */}
        <form className={styles.form} onSubmit={handleLogin}>
          {/* Username Field */}
          <div className={styles.field}>
            <Label className={styles.label} required>
              Username
            </Label>
            <Input
              className={styles.input}
              type="text"
              placeholder="Enter your username"
              value={formData.username}
              onChange={e => handleChange('username', e.target.value)}
              contentBefore={<PersonRegular />}
              size="large"
              required
              disabled={loading}
            />
          </div>

          {/* Password Field */}
          <div className={styles.field}>
            <Label className={styles.label} required>
              Password
            </Label>
            <div className={styles.passwordContainer}>
              <Input
                className={styles.input}
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={formData.password}
                onChange={e => handleChange('password', e.target.value)}
                contentBefore={<LockClosedRegular />}
                size="large"
                required
                disabled={loading}
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOffRegular /> : <EyeRegular />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            className={styles.submitButton}
            appearance="primary"
            type="submit"
            size="large"
            disabled={loading}
            icon={loading ? <Spinner size="tiny" /> : undefined}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>

        {/* Footer */}
        <div className={styles.footer}>
          <Text>© 2025 Security Management System. All rights reserved.</Text>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;
